// File: /opt/stacks/workout/app/src/hooks/useAudioSettings.js
//
// localStorage-persisted audio preferences with optional API sync.
// Deliberately NOT a React context — only two consumers (useSound + AudioSettings UI).
// Reading localStorage directly in playSound avoids all re-render overhead.

import { useState, useCallback } from "react";
import {
  DEFAULT_PROFILE,
  SOUND_PROFILES,
  resolveSoundProfileKey,
} from "../config/soundProfiles";
import { DEFAULT_AUDIO_OUTPUT, resolveAudioOutput } from "../audio/audioOutput";

const STORAGE_KEY = "khronos_audio_settings";

export const AUDIO_DEFAULTS = {
  volume: 0.7,
  muted: false,
  profile: DEFAULT_PROFILE,
  countdownEnabled: true,
  hapticEnabled: true,
  countInDuration: 3,
  // Where cues play while one of your Khronos Timers follows the session
  // (issue #45). Client-only (not API-synced) — a per-device choice.
  output: DEFAULT_AUDIO_OUTPUT,
};

export const AUDIO_SYNC_FIELDS = [
  "volume",
  "muted",
  "profile",
  "countdownEnabled",
  "hapticEnabled",
  "countInDuration",
];

// Fields that live only in this browser (no API column). The sync-coverage
// test asserts every default is either synced or listed here.
export const AUDIO_CLIENT_ONLY_FIELDS = ["output"];

/** Read audio settings from localStorage (synchronous, ~0.01ms). */
export function getAudioSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const merged = { ...AUDIO_DEFAULTS, ...JSON.parse(raw) };
      const profile = resolveSoundProfileKey(merged.profile);

      return {
        ...merged,
        profile: SOUND_PROFILES[profile] ? profile : DEFAULT_PROFILE,
        output: resolveAudioOutput(merged.output),
      };
    }
  } catch {
    // corrupted data — fall back to defaults
  }
  return { ...AUDIO_DEFAULTS };
}

/** Write audio settings to localStorage. */
function saveAudioSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage full or unavailable — fail silently
  }
}

/**
 * Mute or unmute the cues from outside the settings screen (the Android
 * app's notification). Local to this device, like a volume key.
 */
export function setAudioMuted(muted) {
  saveAudioSettings({ ...getAudioSettings(), muted: !!muted });
}

/**
 * Merge server-fetched settings into localStorage.
 * Server values win over local values.
 */
export function mergeAudioSettingsFromServer(serverSettings) {
  if (!serverSettings) return;
  const current = getAudioSettings();
  const merged = { ...current };

  if (serverSettings.audio_volume != null)
    merged.volume = serverSettings.audio_volume;
  if (serverSettings.audio_muted != null)
    merged.muted = serverSettings.audio_muted;
  if (serverSettings.audio_profile != null)
    merged.profile = serverSettings.audio_profile;
  if (serverSettings.audio_countdown_enabled != null)
    merged.countdownEnabled = serverSettings.audio_countdown_enabled;
  if (serverSettings.audio_haptic_enabled != null)
    merged.hapticEnabled = serverSettings.audio_haptic_enabled;
  if (serverSettings.audio_count_in_duration != null)
    merged.countInDuration = serverSettings.audio_count_in_duration;

  saveAudioSettings(merged);
  return merged;
}

/**
 * Convert local audio settings to API field names for syncing.
 */
export function audioSettingsToApi(settings) {
  return {
    audio_volume: settings.volume,
    audio_muted: settings.muted,
    audio_profile: settings.profile,
    audio_countdown_enabled: settings.countdownEnabled,
    audio_haptic_enabled: settings.hapticEnabled,
    audio_count_in_duration: settings.countInDuration,
  };
}

/**
 * React hook for the AudioSettings UI component.
 * Returns [settings, updateSettings] where updateSettings accepts a partial update.
 * Optional onSync callback is called with API-shaped data on each change.
 */
const useAudioSettings = ({ onSync } = {}) => {
  const [settings, setSettings] = useState(getAudioSettings);

  const updateSettings = useCallback(
    (partial) => {
      setSettings((prev) => {
        const next = {
          ...prev,
          ...partial,
          profile: partial.profile
            ? resolveSoundProfileKey(partial.profile)
            : prev.profile,
        };
        saveAudioSettings(next);
        onSync?.(audioSettingsToApi(next));
        return next;
      });
    },
    [onSync],
  );

  return [settings, updateSettings];
};

export default useAudioSettings;
